import { Component, OnInit, OnDestroy, Input, ViewChild } from "@angular/core";
import { Subscription } from "rxjs";
import * as L from "leaflet";
import { environment } from "../../../environments/environment";
import { Utils } from "../../common/utils";
import { MapBaseComponent } from "../../common/map-base/map-base.component";
import { ContextService } from "../../services/context.service";
import { IEvent } from "../../services/match.service";
import { StatsService } from "../../services/stats.service";
import { ISelectedPlayer } from "../players-list/players-list.component";

import courtGeom from "../../../assets/data/court.json";
import zoneLinesGeom from "../../../assets/data/zone_lines.json";
import zonesGeom from "../../../assets/data/zone.json";

@Component({
  selector: "app-map-zone",
  templateUrl: "./map-zones.component.html",
  styleUrls: [
    "../../common/map-base/map-base.component.scss",
    "./map-zones.component.scss",
  ],
})
export class MapZonesComponent
  extends MapBaseComponent
  implements OnInit, OnDestroy
{
  _events: IEvent[] = [];
  _teams: any = {};
  _mode = "zone";
  _playersSelected: ISelectedPlayer[] = [];

  @Input() get teams(): any {
    return this._teams;
  }
  set teams(teams: any) {
    this._teams = teams;
    this.indexTeams();
    this.refreshMap();
  }
  @Input() get events(): IEvent[] {
    return this._events;
  }
  set events(events: IEvent[]) {
    this._events = events;
    this.refreshMap();
  }
  @Input() get mode(): string {
    return this._mode;
  }
  set mode(mode: string) {
    this._mode = mode;
    this.refreshMap();
  }
  @Input() get playersSelected(): ISelectedPlayer[] {
    return this._playersSelected;
  }
  set playersSelected(playersSelected: ISelectedPlayer[]) {
    this._playersSelected = playersSelected.filter((elem) => elem);
    this.refreshMap();
  }

  @ViewChild("map", { static: true }) mapElem: any;

  map: any;

  zonesLayer: any;
  zoneMarkersLayer: any;

  teamsIndex = {};
  playersIndex = {};
  zoneStats: any;

  popupStats = {};

  utils = Utils;

  constructor(context: ContextService, statsService: StatsService) {
    super(statsService, context);
  }

  ngOnInit() {
    super.ngOnInit();
    this.refreshMap();
  }

  drawBaseMap() {
    this.drawZones(this.rotateGeomIfNeeded(zonesGeom));
    this.drawCourt(courtGeom, zoneLinesGeom);
  }

  private indexTeams() {
    this.teamsIndex = {};
    this.playersIndex = {};
    if (this.teams.home) {
      this.teamsIndex[this.teams.home.id] = {
        code: this.teams.home.code,
        name: this.teams.home.name,
      };
      this.playersIndex = this.teams.home.players.reduce((acum, elem) => {
        acum[elem.id] = elem;
        return acum;
      }, {});
    }
    if (this.teams.visitor) {
      this.teamsIndex[this.teams.visitor.id] = {
        code: this.teams.visitor.code,
        name: this.teams.visitor.name,
      };
      this.playersIndex = this.teams.visitor.players.reduce((acum, elem) => {
        acum[elem.id] = elem;
        return acum;
      }, this.playersIndex);
    }
  }

  refreshMap() {
    if (this.map && this._events.length && this._teams.home) {
      const playersSelectedId = this._playersSelected.reduce((acum, player) => {
        acum.push(player.id);
        return acum;
      }, []);
      switch (this._mode) {
        case "zone":
          this.zoneStats = this.statsService.calculateZoneStats(
            this.events,
            this.teams,
            this.context.getQuarter(),
            playersSelectedId
          );
          this.paintStats();
          break;
        case "zoneLeader":
          this.zoneStats = this.statsService.calculateZoneLeader(
            this.events,
            this.teams,
            this.context.getQuarter(),
            playersSelectedId
          );
          this.paintLeader();
          break;
        default:
          console.error("Map mode not found");
      }
    }
  }

  drawZones(data) {
    this.zonesLayer = L.geoJSON(data, {
      style: function (feature) {
        return {
          weight: 0,
          fillColor: "#FFF",
          fillOpacity: 1,
        };
      },
      smoothFactor: 0,
    }).addTo(this.map);
    this.zoneMarkersLayer = L.layerGroup().addTo(this.map);
  }

  paintStats() {
    const playerSelectedMap = this.playersSelected.reduce(
      (map, currentPlayer: any) => {
        const id = currentPlayer.id;
        const player = this.playersIndex[id];
        if (player.team_id === this._teams.home.id) {
          map.home = id;
        }
        if (player.team_id === this._teams.visitor.id) {
          map.visitor = id;
        }
        return map;
      },
      { home: null, visitor: null }
    );

    this.zonesLayer.setStyle((feature) => {
      return {
        fillColor: this.statsService.getAccuracyColor(
          this.zoneStats[feature.properties.id]
        ),
      };
    });
    this.zoneMarkersLayer.clearLayers();
    this.zonesLayer.eachLayer((layer) => {
      let stats = this.zoneStats[layer.feature.properties.id];
      let statsOpponent = this.zoneStats[layer.feature.properties.op_zone_id];
      const layerIsHome = layer.feature.properties.team === "home";
      const layerIsVisitor = layer.feature.properties.team === "visitor";
      const shouldPaintHome =
        !this._playersSelected.length || playerSelectedMap.home;
      const shouldPaintVisitor =
        !this.playersSelected.length || playerSelectedMap.visitor;
      if (
        (layerIsHome && shouldPaintHome) ||
        (layerIsVisitor && shouldPaintVisitor)
      ) {
        let center;
        if (layer.feature.geometry.type === "Point") {
          center = [
            layer.feature.geometry.coordinates[1],
            layer.feature.geometry.coordinates[0],
          ];
        } else {
          center = layer.getCenter();
        }
        const icon = this.createStatMarkerIcon(layer.feature.properties.id);
        const marker = L.marker(center, { icon: icon });
        stats = { ...stats };
        statsOpponent = { ...statsOpponent };
        stats.accuracy = this.statsService.calculateAccuracy(stats);
        statsOpponent.accuracy =
          this.statsService.calculateAccuracy(statsOpponent);
        stats.accuracyColor = this.statsService.getAccuracyColor(
          this.zoneStats[layer.feature.properties.id]
        );
        statsOpponent.accuracyColor = this.statsService.getAccuracyColor(
          this.zoneStats[layer.feature.properties.op_zone_id]
        );

        if (layerIsHome) {
          stats.name = this.teams.home.name;
          statsOpponent.name = this.teams.visitor.name;
          stats.player = this.playersIndex[playerSelectedMap.home];
          statsOpponent.player = this.playersIndex[playerSelectedMap.visitor];
        } else {
          stats.name = this.teams.visitor.name;
          statsOpponent.name = this.teams.home.name;
          stats.player = this.playersIndex[playerSelectedMap.visitor];
          statsOpponent.player = this.playersIndex[playerSelectedMap.home];
        }

        const popupStats = {
          home: {
            name: stats.name,
            points: stats.points || 0,
            shots: stats.shots || 0,
            failures: stats.failures || 0,
            accuracy: stats.accuracy || 0,
            accuracyColor: stats.accuracyColor || "#FFF",
            player: stats.player || null,
            team: stats.team || null,
          },
          visitor: {
            name: statsOpponent.name,
            points: statsOpponent.points || 0,
            shots: statsOpponent.shots || 0,
            failures: statsOpponent.failures || 0,
            accuracy: statsOpponent.accuracy || 0,
            accuracyColor: statsOpponent.accuracyColor || "#FFF",
            player: statsOpponent.player || null,
            team: statsOpponent.team || null,
          },
        };
        marker.on("mouseover", (e) => {
          this.popupStats = popupStats;
          this.openPopup(e);
        });
        marker.on("mouseout", (e) => {
          this.closePopup(e);
        });
        this.zoneMarkersLayer.addLayer(marker);
      }
    });
  }

  paintLeader() {
    this.zonesLayer.setStyle((feature) => {
      return {
        fillColor: "#FFF",
      };
    });
    this.zoneMarkersLayer.clearLayers();
    this.zonesLayer.eachLayer((layer) => {
      if (
        this.zoneStats[layer.feature.properties.id] &&
        this.zoneStats[layer.feature.properties.id].leader
      ) {
        let center;
        if (layer.feature.geometry.type === "Point") {
          center = [
            layer.feature.geometry.coordinates[1],
            layer.feature.geometry.coordinates[0],
          ];
        } else {
          center = layer.getCenter();
        }
        const icon = this.createLeaderMarkerIcon(
          layer.feature.properties.id,
          layer.feature.properties.labelside === "left"
        );
        const marker = L.marker(center, { icon: icon });
        const stats = Object.assign(
          {},
          this.zoneStats[layer.feature.properties.id]
        );
        stats.accuracy = this.statsService.calculateAccuracy(stats);
        stats.player = this.playersIndex[stats.leader];
        stats.team = stats.player
          ? this.teamsIndex[stats.player.team_id]
          : null;

        marker.on("mouseover", (e) => {
          this.popupStats = {
            player: stats.player,
            team: stats.team,
            points: stats.points || 0,
            shots: stats.shots || 0,
            failures: stats.failures || 0,
            accuracy: stats.accuracy || 0,
          };
          this.openPopup(e);
        });
        marker.on("mouseout", (e) => {
          this.closePopup(e);
        });
        this.zoneMarkersLayer.addLayer(marker);
      }
    });
  }

  private getZoneColor(zoneId) {
    const step = 100 / 5;
    let value;
    if (this.zoneStats[zoneId]) {
      value = this.statsService.calculateAccuracy(this.zoneStats[zoneId]);
    } else {
      value = 0;
    }

    if (value === 0) {
      return "#FFFFFF";
    } else if (value < step) {
      return "#FFFFCC";
    } else if (value < step * 2) {
      return "#A1DAB4";
    } else if (value < step * 3) {
      return "#41B6C4";
    } else if (value < step * 4) {
      return "#2C7FB8";
    } else {
      return "#253494";
    }
  }

  private getZoneClassByAcc(accuracy) {
    const step = 100 / 5;
    let zone;
    if (accuracy === 0) {
      zone = 0;
    } else if (accuracy < step) {
      zone = 1;
    } else if (accuracy < step * 2) {
      zone = 2;
    } else if (accuracy < step * 3) {
      zone = 3;
    } else if (accuracy < step * 4) {
      zone = 4;
    } else {
      zone = 5;
    }
    return `acc${zone}`;
  }

  private createStatMarkerIcon(zoneId) {
    let points = 0;
    let accuracy = 0;
    if (this.zoneStats[zoneId]) {
      points = this.zoneStats[zoneId].points;
      accuracy = this.statsService.calculateAccuracy(this.zoneStats[zoneId]);
    } else {
      points = 0;
      accuracy = 0;
    }

    let colorClass = null;
    if (zoneId > 20) {
      colorClass = this.getZoneClassByAcc(accuracy);
    }

    const icon = L.divIcon({
      className: `marker stat ${
        zoneId < 5 || zoneId > 20 ? " mini" : ""
      } ${colorClass}`,
      html: `<span class="points">${points}</span>
             <span class="accuracy">${accuracy}%</span>`,
    });

    return icon;
  }

  private createLeaderMarkerIcon(zoneId, leftSide) {
    let points = 0;
    let leader: any = null;
    let team: any = null;
    if (this.zoneStats[zoneId]) {
      points = this.zoneStats[zoneId].points;
      leader = this.playersIndex[this.zoneStats[zoneId].leader];
      team = this.teamsIndex[leader.team_id];
    }

    const icon = L.divIcon({
      className: `marker leader ${leftSide ? "leftSide" : ""} ${
        zoneId < 5 || zoneId > 20 ? " mini" : ""
      }`,
      // tslint:disable-next-line:max-line-length
      html: ` <div
                class="photo"
                style="background-image: url('${environment.teamAssetsUrl}/${
        team ? team.code.toLowerCase() : "default"
      }/${leader ? leader.id : "no"}.jpg')">
              </div>
              <span class="initials">${
                leader ? this.utils.getPlayerInitials(leader.name) : ""
              }</span>
              <span class="points">${points}</span>`,
    });

    return icon;
  }
}
